// Formatting commands — verbatim port of the reference `mdApply` (logic.split.js L728–746, inventory F14) as a pure
// transform: (value, selectionStart, selectionEnd) → (value, selection). Placeholders are the reference strings
// (localised through t() by the caller only if desired; the reference inserts English placeholders).

export type FormatKind = 'b' | 'i' | 's' | 'h1' | 'h2' | 'ul' | 'ol' | 'task' | 'quote' | 'code' | 'block' | 'link' | 'hr';

export interface FormatResult { value: string; start: number; end: number }

/** Toolbar buttons in reference order; `sep` marks the 1×18px separators. Titles are the reference strings. */
export interface SvgEl { tag: 'path' | 'circle' | 'rect'; attrs: Record<string, string | number> }
const P = (d: string): SvgEl => ({ tag: 'path', attrs: { d } });
const Ci = (cx: number, cy: number, r: number): SvgEl => ({ tag: 'circle', attrs: { cx, cy, r } });
const R = (x: number, y: number, width: number, height: number, rx: number): SvgEl => ({ tag: 'rect', attrs: { x, y, width, height, rx } });

export const TOOLBAR: ReadonlyArray<{ k: FormatKind; title: string; icon: SvgEl[]; sep?: true }> = [
  { k: 'b', title: 'Bold (⌘B)', icon: [P('M7 5h6a3.5 3.5 0 0 1 0 7H7z'), P('M7 12h7a3.5 3.5 0 0 1 0 7H7z')] },
  { k: 'i', title: 'Italic (⌘I)', icon: [P('M14 5h-4'), P('M14 19h-4'), P('M14 5l-4 14')] },
  { k: 's', title: 'Strikethrough', icon: [P('M5 12h14'), P('M16 7.5C15.3 6 13.8 5 12 5c-2.2 0-4 1.3-4 3.2 0 1.5 1 2.4 3 3'), P('M8 16.5c.7 1.5 2.2 2.5 4 2.5 2.2 0 4-1.3 4-3.2')] },
  { k: 'h1', title: 'Heading 1', sep: true, icon: [P('M4 6v12'), P('M12 6v12'), P('M4 12h8'), P('M17 10l2-1.5V18')] },
  { k: 'h2', title: 'Heading 2', icon: [P('M4 6v12'), P('M12 6v12'), P('M4 12h8'), P('M16 10.5a2 2 0 0 1 4 .5c0 2-4 3.5-4 7h4')] },
  { k: 'ul', title: 'Bulleted list', sep: true, icon: [P('M9 6h11'), P('M9 12h11'), P('M9 18h11'), Ci(4.5, 6, 1), Ci(4.5, 12, 1), Ci(4.5, 18, 1)] },
  { k: 'ol', title: 'Numbered list', icon: [P('M10 6h10'), P('M10 12h10'), P('M10 18h10'), P('M4 5l1.5-1V9'), P('M3.5 14.5a1.3 1.3 0 0 1 2.5.4c0 1-2.5 2-2.5 3.6H6')] },
  { k: 'task', title: 'Checklist', icon: [R(3, 4, 6, 6, 1), P('M4.5 7l1 1 2-2'), R(3, 14, 6, 6, 1), P('M13 7h8'), P('M13 17h8')] },
  { k: 'quote', title: 'Quote', icon: [P('M6 17c-1.5 0-2.5-1-2.5-2.8C3.5 10.5 6 8 8.5 7'), P('M15 17c-1.5 0-2.5-1-2.5-2.8 0-3.7 2.5-6.2 5-7.2')] },
  { k: 'code', title: 'Inline code', sep: true, icon: [P('M9 8l-4 4 4 4'), P('M15 8l4 4-4 4')] },
  { k: 'block', title: 'Code block', icon: [R(3, 4, 18, 16, 2), P('M10 10l-2 2 2 2'), P('M14 10l2 2-2 2')] },
  { k: 'link', title: 'Link (⌘K)', icon: [P('M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1'), P('M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1')] },
  { k: 'hr', title: 'Divider', icon: [P('M4 12h16')] },
];

export function applyFormat(k: FormatKind, v: string, a: number, b: number): FormatResult {
  const sel = v.slice(a, b);
  let nv = v, na = a, nb = b;
  const wrap = (pre: string, post: string, ph: string) => { const t = sel || ph; nv = v.slice(0, a) + pre + t + post + v.slice(b); na = a + pre.length; nb = na + t.length; };
  const lines = (fn: (blk: string[]) => string[]) => {
    const ls = v.lastIndexOf('\n', a - 1) + 1, le = v.indexOf('\n', b) < 0 ? v.length : v.indexOf('\n', b);
    const blk = v.slice(ls, le).split('\n');
    const out = fn(blk).join('\n'); nv = v.slice(0, ls) + out + v.slice(le); na = ls; nb = ls + out.length;
  };
  const pref = (re: RegExp, mk: (i: number) => string) => lines(blk => {
    const all = blk.every(l => re.test(l));
    return blk.map((l, i) => all ? l.replace(re, '') : mk(i) + l.replace(/^(#{1,6} |> |[-*+] \[[ x]\] |[-*+] |\d+\. )/, ''));
  });
  if (k === 'b') wrap('**', '**', 'bold text');
  else if (k === 'i') wrap('*', '*', 'italic text');
  else if (k === 's') wrap('~~', '~~', 'text');
  else if (k === 'code') wrap('`', '`', 'code');
  else if (k === 'link') {
    if (sel && /^https?:\/\//.test(sel)) { nv = v.slice(0, a) + '[link](' + sel + ')' + v.slice(b); na = a + 1; nb = a + 5; }
    else { wrap('[', '](https://)', 'link text'); na = nb + 2; nb = na + 8; }
  }
  else if (k === 'block') { const pre = (a > 0 && v[a - 1] !== '\n' ? '\n' : '') + '```\n'; wrap(pre, '\n```\n', 'code'); }
  else if (k === 'hr') { const pre = (a > 0 && v[a - 1] !== '\n' ? '\n' : '') + '\n---\n\n'; nv = v.slice(0, a) + pre + v.slice(b); na = nb = a + pre.length; }
  else if (k === 'h1') pref(/^# /, () => '# ');
  else if (k === 'h2') pref(/^## /, () => '## ');
  else if (k === 'ul') pref(/^[-*+] (?!\[)/, () => '- ');
  else if (k === 'ol') pref(/^\d+\. /, i => (i + 1) + '. ');
  else if (k === 'task') pref(/^[-*+] \[[ x]\] /, () => '- [ ] ');
  else if (k === 'quote') pref(/^> /, () => '> ');
  return { value: nv, start: na, end: nb };
}

/** ⌘/Ctrl+B, ⌘/Ctrl+I, ⌘/Ctrl+K (reference `mdOnKey`; both modifiers accepted on every platform). */
export function shortcutFor(e: { key: string; metaKey: boolean; ctrlKey: boolean; altKey?: boolean }): FormatKind | null {
  if (!(e.metaKey || e.ctrlKey)) return null;
  return ({ b: 'b', i: 'i', k: 'link' } as Record<string, FormatKind>)[e.key.toLowerCase()] ?? null;
}

/** Keys allowed while an earlier version is shown read-only (reference `mdOnKey` history branch). */
export function isNavigationKey(e: { key: string; metaKey: boolean; ctrlKey: boolean }): boolean {
  return /^(Arrow|Page|Home|End|Tab|Escape|Shift|Alt|Meta|Control|CapsLock|F\d)/.test(e.key) || ((e.metaKey || e.ctrlKey) && /^[ac]$/i.test(e.key));
}

/** "⌘" on Apple platforms, "Ctrl+" elsewhere (inventory F14 production note). */
export function localizeShortcut(title: string, apple: boolean): string {
  return apple ? title : title.replace('⌘', 'Ctrl+');
}

export const isApplePlatform = (): boolean => typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test((navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform || navigator.platform || navigator.userAgent || '');
